"use client";

import type { Locale } from "@/i18n/routing";
import type { CurrencyInfo } from "@/lib/currency-format";
import type { LocalizedText } from "@/lib/validation/json";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { Link, useRouter } from "@/i18n/navigation";
import { formatMoney } from "@/lib/currency-format";
import { pickLocale } from "@/lib/i18n-content";

interface Suggestion {
	slug: string;
	name: LocalizedText;
	price: number;
	image: string | null;
	available?: boolean;
}

interface CategorySuggestion {
	slug: string;
	name: LocalizedText;
}

/** Wrap the first case-insensitive occurrence of `q` in <mark> for highlight. */
function highlight(text: string, q: string): React.ReactNode {
	const term = q.trim();
	if (!term) return text;
	const i = text.toLowerCase().indexOf(term.toLowerCase());
	if (i < 0) return text;
	return (
		<>
			{text.slice(0, i)}
			<mark className="bg-primary/20 text-inherit">{text.slice(i, i + term.length)}</mark>
			{text.slice(i + term.length)}
		</>
	);
}

export function SearchBar({
	currency,
	factor,
	mobile = false,
}: {
	currency: CurrencyInfo;
	factor: number;
	/** Full-width variant rendered in the mobile search row (12.11). */
	mobile?: boolean;
}) {
	const t = useTranslations("shop");
	const locale = useLocale() as Locale;
	const router = useRouter();
	const params = useSearchParams();
	const [value, setValue] = useState(params.get("q") ?? "");
	const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
	const [catSuggestions, setCatSuggestions] = useState<CategorySuggestion[]>([]);
	const [availableOnly, setAvailableOnly] = useState(false);
	const [searched, setSearched] = useState(false);
	const [open, setOpen] = useState(false);
	const boxRef = useRef<HTMLDivElement>(null);

	// Debounced suggestions fetch.
	useEffect(() => {
		const term = value.trim();
		if (term.length < 2) {
			setSuggestions([]);
			setSearched(false);
			return;
		}
		const ctrl = new AbortController();
		const id = setTimeout(async () => {
			try {
				const res = await fetch(`/api/search?q=${encodeURIComponent(term)}`, {
					signal: ctrl.signal,
				});
				// A throttled/failed request must NOT render as "no results" — that
				// would be a deceptive empty state. Only trust 2xx responses.
				if (!res.ok) return;
				const json = await res.json();
				setSuggestions(json.data ?? []);
				setCatSuggestions(json.categories ?? []);
				setSearched(true);
				setOpen(true);
			} catch {
				/* ignore */
			}
		}, 250);
		return () => {
			clearTimeout(id);
			ctrl.abort();
		};
	}, [value]);

	// Close on outside click.
	useEffect(() => {
		function onClick(e: MouseEvent) {
			if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", onClick);
		return () => document.removeEventListener("mousedown", onClick);
	}, []);

	function submit(e: React.FormEvent) {
		e.preventDefault();
		setOpen(false);
		const next = new URLSearchParams();
		if (value.trim()) next.set("q", value.trim());
		router.push(`/?${next.toString()}`);
	}

	return (
		<div
			ref={boxRef}
			className={mobile ? "relative block w-full" : "relative hidden flex-1 sm:block"}
		>
			<form onSubmit={submit}>
				<input
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onFocus={() => searched && setOpen(true)}
					placeholder={t("search")}
					className="w-full rounded-lg border border-border bg-surface py-2 ps-9 pe-3 text-sm outline-none focus:border-primary"
				/>
				<Icon
					icon={faMagnifyingGlass}
					size={16}
					className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
				/>
			</form>

			{open && searched && value.trim().length >= 2 && (
				<div className="absolute z-40 mt-1 w-full overflow-hidden rounded-lg border border-border bg-surface shadow-[var(--shadow)]">
					{/* Category autosuggestions (12.14 Future) */}
					{catSuggestions.length > 0 && (
						<div className="flex flex-wrap gap-1.5 border-b border-border px-3 py-2">
							{catSuggestions.map((c) => (
								<Link
									key={c.slug}
									href={`/?category=${c.slug}`}
									onClick={() => setOpen(false)}
									className="rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs hover:border-primary/40 hover:text-primary"
								>
									{t("inCategory")}:{pickLocale(c.name, locale)}
								</Link>
							))}
						</div>
					)}

					{/* In-search filter (12.14 Future): available only */}
					{suggestions.some((s) => s.available === false) && (
						<label className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
							<input
								type="checkbox"
								checked={availableOnly}
								onChange={(e) => setAvailableOnly(e.target.checked)}
							/>
							{t("searchAvailableOnly")}
						</label>
					)}

					{(() => {
						const shown = availableOnly
							? suggestions.filter((s) => s.available !== false)
							: suggestions;
						return shown.length > 0 ? (
							shown.map((s) => (
								<Link
									key={s.slug}
									href={`/products/${s.slug}`}
									onClick={() => setOpen(false)}
									className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-surface-2"
								>
									<span className="h-9 w-9 shrink-0 overflow-hidden rounded bg-surface-2">
										{s.image && (
											// oxlint-disable-next-line nextjs/no-img-element -- suggestion thumbnail, merchant-uploaded at arbitrary dimensions
											<img
												src={s.image}
												alt=""
												className={`h-full w-full object-cover ${s.available === false ? "opacity-50" : ""}`}
											/>
										)}
									</span>
									<span className="flex-1 truncate">
										{highlight(pickLocale(s.name, locale), value)}
										{s.available === false && (
											<span className="ms-2 text-xs text-warning">· {t("unavailableNow")}</span>
										)}
									</span>
									<span className="shrink-0 text-primary">
										{formatMoney(s.price * factor, currency)}
									</span>
								</Link>
							))
						) : (
							<div className="flex flex-col gap-1 px-3 py-4 text-center text-sm">
								<span className="text-muted-foreground">{t("searchNoResults")}</span>
								<Link
									href="/"
									onClick={() => setOpen(false)}
									className="text-primary hover:underline"
								>
									{t("searchBrowse")}
								</Link>
							</div>
						);
					})()}
				</div>
			)}
		</div>
	);
}
